import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, ShieldCheck, ArrowRight, CheckCircle2, Sparkles, Camera, LineChart, Utensils, MessageCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../context/ClientAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'bodyup_client_token';

const FEATURES = [
  { icon: Camera, fr: 'Scan de repas par l\u2019IA en 1 clic', en: 'AI meal photo scan in 1 click' },
  { icon: LineChart, fr: 'Suivi de progression intelligent', en: 'Smart progress tracking' },
  { icon: Utensils, fr: 'Plans alimentaires sur mesure', en: 'Custom meal plans' },
  { icon: MessageCircle, fr: 'Messagerie coach prioritaire', en: 'Priority coach messaging' },
];

/**
 * Carte de conversion #1 du parcours calculateur → compte + essai. Affichée à TOUS les
 * leads (payants ou non) juste après les résultats gratuits, AVANT tout paywall — c'est
 * le seul et unique chemin proposé pour créer un compte (remplace AccountUpsell +
 * CoachingBridge, consolidés ici le 15/09/2026 pour un parcours plus clair).
 * 1 clic : inscription (email pré-rempli) → lance directement le paiement Stripe (essai
 * 7 jours, carte requise dès l'inscription, facturation auto après l'essai sauf annulation).
 */
export const FreeTrialSignupCard = ({ email, language = 'fr' }) => {
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);
  const { isAuthed, register } = useClientAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [exists, setExists] = useState(false);

  if (isAuthed) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setExists(false);
    setBusy(true);
    try {
      await register(email, password, '', language);
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${BACKEND_URL}/api/tracking/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: 'monthly', origin_url: window.location.origin }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url) {
        window.location.href = data.url;
        return;
      }
      toast.error(L(
        "Compte créé, mais le paiement n'a pas pu démarrer. Réessayez depuis la page Suivi Pro.",
        'Account created, but payment could not start. Please retry from the Suivi Pro page.'
      ));
      navigate('/suivi-pro');
    } catch (err) {
      if (err.status === 409) setExists(true);
      else setError(err.message);
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="relative mt-10 mb-12 rounded-3xl border border-white/10 overflow-hidden shadow-[0_20px_48px_rgba(233,30,99,0.15)]"
      style={{ background: 'linear-gradient(180deg, rgba(26,26,36,0.95), rgba(10,10,12,0.98))' }}
      data-testid="signup-trial-card"
    >
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-yellow-400" />

      <div className="grid md:grid-cols-12 gap-8 p-6 md:p-10">
        {/* Colonne gauche : promesse + fonctionnalités débloquées */}
        <motion.div
          initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="md:col-span-7 flex flex-col justify-center"
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-bold text-yellow-300 tracking-wide mb-3 w-fit">
            <Sparkles className="w-3.5 h-3.5" /> {L('7 JOURS GRATUITS', '7-DAY FREE TRIAL')}
          </div>
          <h3 className="calc-display text-2xl md:text-3xl font-bold tracking-tight text-white leading-tight">
            {L('Vos résultats sont prêts.', 'Your results are ready.')}<br />
            <span className="bg-gradient-to-r from-primary to-yellow-400 bg-clip-text text-transparent">
              {L('Passez à la vitesse supérieure.', 'Take it to the next level.')}
            </span>
          </h3>
          <p className="calc-premium text-zinc-300 text-[15px] md:text-base leading-relaxed mt-3 max-w-md">
            {L(
              'Créez votre compte gratuit pour garder ces résultats et débloquer toute l\u2019application pendant 7 jours, gratuitement.',
              'Create your free account to keep these results and unlock the full app for 7 days, free.'
            )}
          </p>
          <ul className="mt-5 space-y-2.5">
            {FEATURES.map((f, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.35, delay: 0.15 + i * 0.08 }}
                className="flex items-center gap-3 text-zinc-200 text-sm hover:translate-x-1 transition-transform"
              >
                <span className="calc-icon-chip w-8 h-8 rounded-lg"><f.icon className="w-4 h-4" strokeWidth={2.5} /></span>
                {L(f.fr, f.en)}
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* Colonne droite : formulaire d'inscription + essai */}
        <motion.div
          initial={{ opacity: 0, x: 16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="md:col-span-5 bg-white/[0.03] rounded-2xl p-6 border border-white/10 flex flex-col gap-4 relative overflow-hidden"
        >
          {exists ? (
            <div className="rounded-xl bg-black/40 border border-white/10 p-4 text-sm text-zinc-300" data-testid="signup-exists">
              {L('Un compte existe déjà avec cet email.', 'An account already exists with this email.')}{' '}
              <Link to="/espace-client" className="text-primary font-semibold hover:underline">{L('Se connecter', 'Log in')}</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-4" data-testid="signup-trial-form">
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">{L('Votre email', 'Your email')}</label>
                <input value={email || ''} readOnly className="auth-input opacity-70 bg-black/40 cursor-not-allowed" data-testid="signup-email-input" aria-label="email" />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">{L('Choisissez un mot de passe', 'Choose a password')}</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="password" required minLength={6} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" className="auth-input pl-9 focus:ring-2 focus:ring-primary/50" data-testid="signup-password-input"
                    aria-label={L('Mot de passe', 'Password')}
                  />
                </div>
              </div>
              <button
                type="submit" disabled={busy}
                className="relative overflow-hidden cta-shimmer h-[50px] rounded-xl calc-display font-bold text-white text-base tracking-wide bg-gradient-to-r from-primary to-yellow-400 hover:-translate-y-1 hover:shadow-[0_8px_20px_rgba(233,30,99,0.4)] active:scale-95 transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="signup-submit-button"
              >
                {busy ? L('Création en cours…', 'Creating…') : (
                  <>{L('Démarrer mes 7 jours gratuits', 'Start my 7-day free trial')} <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
              {error && <p className="text-sm text-red-400" data-testid="signup-error">{error}</p>}
            </form>
          )}

          <div className="flex flex-col gap-2 pt-1">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-400 text-xs w-fit">
              <ShieldCheck className="w-3.5 h-3.5 text-green-400" /> {L('Paiement Stripe sécurisé', 'Secure Stripe payment')}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-400 text-xs w-fit">
              <Check className="w-3.5 h-3.5 text-green-400" /> {L('Sans engagement — annulez à tout moment', 'No commitment — cancel anytime')}
            </span>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default FreeTrialSignupCard;
